import React from "react";
import type { INumberedFaqItem, TFaqLang } from "@/src/features/faq/types";

/** Per-surface sizing from the design (mobile 390 → desktop 1280). */
type TFaqItemVariant = "home" | "page" | "listing";

const VARIANTS: Record<
  TFaqItemVariant,
  {
    card: string;
    button: string;
    question: string;
    icon: string;
    iconText: string;
    answer: string;
  }
> = {
  home: {
    card: "rounded-[15px] md:rounded-[18px]",
    button: "gap-3.5 px-[18px] py-[17px] md:gap-5 md:px-[26px] md:py-[22px]",
    question: "text-15 leading-[1.42] md:text-16 md:leading-[1.45]",
    icon: "h-[25px] w-[25px] md:h-[28px] md:w-[28px]",
    iconText: "text-16 md:text-18",
    answer:
      "px-[18px] pb-[18px] text-15 md:pb-6 md:pl-[47px] md:pr-[66px] md:text-16",
  },
  page: {
    card: "rounded-[15px] md:rounded-[16px]",
    button: "gap-3.5 px-[18px] py-4 md:gap-5 md:px-6 md:py-5",
    question: "text-15 leading-[1.42] md:text-16 md:leading-[1.45]",
    icon: "h-[25px] w-[25px] md:h-[28px] md:w-[28px]",
    iconText: "text-16 md:text-18",
    answer: "px-[18px] pb-[18px] text-15 md:px-[62px] md:pb-[22px] md:text-16",
  },
  listing: {
    card: "rounded-[15px]",
    button: "gap-3.5 px-[18px] py-4 md:gap-[18px] md:px-[22px] md:py-[18px]",
    question: "text-15 leading-[1.42] md:text-16 md:leading-[1.45]",
    icon: "h-[25px] w-[25px] md:h-[26px] md:w-[26px]",
    iconText: "text-16 md:text-17",
    answer:
      "px-[18px] pb-[18px] text-15 md:pb-5 md:pl-[50px] md:pr-[54px] md:text-16",
  },
};

interface IFaqAccordionItemProps {
  item: INumberedFaqItem;
  lang: TFaqLang;
  isOpen: boolean;
  onToggle: () => void;
  /** Heading level for the question — keeps the document outline gap-free. */
  headingLevel: "h3" | "h4";
  variant?: TFaqItemVariant;
  /** Show the "01" counter (desktop only, as in the design). */
  showNumber?: boolean;
  /** Position within its list — drives the reveal stagger. */
  index?: number;
}

/**
 * One question / answer row.
 *
 * Follows the ARIA accordion pattern: the question is a heading containing the
 * toggle button, and the panel is a labelled region. The answer stays in the
 * DOM when collapsed (grid `0fr → 1fr` animation) so search engines always read
 * it; `aria-hidden` keeps it out of the accessibility tree while hidden.
 */
const FaqAccordionItem: React.FC<IFaqAccordionItemProps> = ({
  item,
  lang,
  isOpen,
  onToggle,
  headingLevel,
  variant = "page",
  showNumber = false,
  index = 0,
}) => {
  const styles = VARIANTS[variant];
  const buttonId = `faq-trigger-${item.id}`;
  const panelId = `faq-panel-${item.id}`;
  const Heading = headingLevel;

  return (
    <li
      id={`faq-${item.id}`}
      data-faq-reveal
      data-faq-index={index}
      className={`scroll-mt-28 overflow-hidden border border-neutral-200 bg-white transition-[border-color,box-shadow] duration-300 hover:border-neutral-300 hover:shadow-[0_14px_34px_-22px_rgba(60,20,35,0.35)] ${styles.card}`}
    >
      <Heading className="m-0">
        <button
          type="button"
          id={buttonId}
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-controls={panelId}
          className={`flex w-full cursor-pointer items-start justify-between bg-transparent text-left text-inherit ${styles.button}`}
        >
          <span className="flex gap-[13px] md:gap-3.5">
            {showNumber && (
              <span
                aria-hidden="true"
                className="hidden flex-none pt-[3px] font-mono-blog text-12 text-ink-faint md:block md:pt-1 md:text-13"
              >
                {item.num}
              </span>
            )}
            <span
              className={`font-semibold tracking-[-0.01em] [text-wrap:pretty] md:tracking-[-0.015em] ${styles.question}`}
            >
              {item.question[lang]}
            </span>
          </span>
          <span
            aria-hidden="true"
            className={`faq-icon flex flex-none items-center justify-center rounded-full bg-neutral-100 font-medium leading-none text-brand-600 ${
              styles.icon
            } ${styles.iconText} ${isOpen ? "rotate-[135deg]" : "rotate-0"}`}
          >
            +
          </span>
        </button>
      </Heading>

      <div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        aria-hidden={!isOpen}
        className={`faq-panel grid ${
          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <p
            className={`faq-answer m-0 font-serif-blog leading-[1.7] text-ink-dark [text-wrap:pretty] ${
              styles.answer
            } ${isOpen ? "opacity-100" : "opacity-0"}`}
          >
            {item.answer[lang]}
          </p>
        </div>
      </div>
    </li>
  );
};

export default FaqAccordionItem;
